import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import Bold from '@tiptap/extension-bold';
import BulletList from '@tiptap/extension-bullet-list';
import Document from '@tiptap/extension-document';
import Dropcursor from '@tiptap/extension-dropcursor';
import Gapcursor from '@tiptap/extension-gapcursor';
import HardBreak from '@tiptap/extension-hard-break';
import Heading from '@tiptap/extension-heading';
import History from '@tiptap/extension-history';
import Link from '@tiptap/extension-link';
import ListItem from '@tiptap/extension-list-item';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import { EmailBlockNodeView } from './EmailBlockNodeView';

/**
 * What an email body may contain, and nothing else. The node names match the
 * stored schema exactly, so `emailBodySchema` parses `editor.getJSON()` directly
 * with no conversion step to keep in step.
 */

/**
 * An attribute written and read through the same `data-` attribute. Declaring only
 * a default leaves Tiptap looking for an attribute the rendered HTML does not
 * carry, so a copy and paste comes back empty -- saying both in one place is what
 * stops the two disagreeing.
 */
const throughDataAttribute = (key: string, attribute: string, fallback = '') => ({
  default: fallback,
  parseHTML: (element: HTMLElement) => element.getAttribute(attribute) ?? fallback,
  renderHTML: (attributes: Record<string, unknown>) => ({ [attribute]: attributes[key] }),
});

/** A value filled in when the email is sent. An atom, so it cannot be part-edited. */
const Variable = Node.create({
  name: 'variable',
  group: 'inline',
  inline: true,
  atom: true,
  addAttributes() {
    return { path: throughDataAttribute('path', 'data-variable') };
  },
  parseHTML() {
    return [{ tag: 'span[data-variable]' }];
  },
  renderHTML({ HTMLAttributes, node }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, { class: 'email-variable' }),
      `{${node.attrs.path}}`,
    ];
  },
});

/** The button: a label the organisation writes, and a target. */
const Cta = Node.create({
  name: 'cta',
  group: 'block',
  atom: true,
  draggable: true,
  addAttributes() {
    // The label rides in an attribute as well as the text: the text of an atom is
    // not parsed back into one, so a paste would otherwise lose the wording.
    return {
      label: throughDataAttribute('label', 'data-cta-label', 'View'),
      target: throughDataAttribute('target', 'data-cta'),
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(EmailBlockNodeView);
  },
  parseHTML() {
    return [{ tag: 'div[data-cta]' }];
  },
  renderHTML({ HTMLAttributes, node }) {
    return ['div', mergeAttributes(HTMLAttributes, { class: 'email-cta' }), `${node.attrs.label}`];
  },
});

/** A block the send path supplies whole. Atomic: placed or removed, not edited. */
const Fragment = Node.create({
  name: 'fragment',
  group: 'block',
  atom: true,
  draggable: true,
  addAttributes() {
    return { name: throughDataAttribute('name', 'data-fragment') };
  },
  addNodeView() {
    return ReactNodeViewRenderer(EmailBlockNodeView);
  },
  parseHTML() {
    return [{ tag: 'div[data-fragment]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, { class: 'email-fragment' }),
      'Course details, filled in when the email is sent',
    ];
  },
});

export const emailEditorExtensions = [
  Document,
  Paragraph,
  Text,
  Bold,
  Heading.configure({ levels: [1, 2] }),
  BulletList,
  // The stored schema holds a hard break, so without this a body carrying one
  // cannot be opened and Shift+Enter does nothing.
  HardBreak,
  // `paragraph+` matches listItemNode; Tiptap's default `paragraph block*` lets a
  // list nest inside a list, which the stored schema rejects.
  //
  // Narrowing alone is not enough: sinkListItem throws rather than declining once
  // its result would be invalid, so Tab is unbound from it. Returning false rather
  // than swallowing the key leaves Tab moving focus out of the editor.
  ListItem.extend({
    content: 'paragraph+',
    addKeyboardShortcuts() {
      return { ...this.parent?.(), Tab: () => false };
    },
  }),
  // Opening in a new tab is the sender's business, not the author's.
  Link.configure({ openOnClick: false }),
  History,
  // Without these there is no caret position before or after an atom, so a body
  // ending in a button could not be typed past.
  Gapcursor,
  Dropcursor,
  Variable,
  Cta,
  Fragment,
];
